/**
 * Customer Management Page
 * Customer profiles, loyalty program, and marketing
 * Connected to backend API
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Users, UserPlus, Search, Star, Gift, TrendingUp, Mail, Phone,
  ChevronRight, Crown, Award, Heart, Calendar, RefreshCcw, Loader2,
  Eye, Edit, Plus, Minus
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import api from '@/services/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const LOYALTY_TIERS = [
  { id: 'bronze', name: 'Bronze', minPoints: 0, color: 'bg-amber-700', icon: Heart },
  { id: 'silver', name: 'Silver', minPoints: 1000, color: 'bg-gray-400', icon: Award },
  { id: 'gold', name: 'Gold', minPoints: 5000, color: 'bg-yellow-500', icon: Star },
  { id: 'platinum', name: 'Platinum', minPoints: 15000, color: 'bg-purple-500', icon: Crown },
];

export function CustomersPage() {
  const { currentOutlet } = useAuth();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [loyaltyStats, setLoyaltyStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('customers');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [pointsDialogOpen, setPointsDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [customerForm, setCustomerForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    marketing_consent: false,
  });
  const [pointsAdjustment, setPointsAdjustment] = useState({ points: '', reason: '' });

  // Fetch customers
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (tierFilter !== 'all') params.append('tier', tierFilter);
      params.append('limit', '100');
      
      const response = await api.get(`/customers?${params.toString()}`);
      setCustomers(response.data || []);
    } catch (err) {
      console.error('Failed to fetch customers:', err);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, tierFilter]);

  // Fetch loyalty stats
  const fetchLoyaltyStats = useCallback(async () => {
    try {
      const response = await api.get('/customers/stats/loyalty-summary');
      setLoyaltyStats(response.data);
    } catch (err) {
      console.error('Failed to fetch loyalty stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
    fetchLoyaltyStats();
  }, [fetchCustomers, fetchLoyaltyStats]);

  // Refresh on search/filter change with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchCustomers();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, tierFilter]);

  // Create customer
  const handleCreateCustomer = async () => {
    if (!customerForm.first_name) {
      toast.error('First name is required');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/customers', customerForm);
      toast.success('Customer created successfully');
      setCreateDialogOpen(false);
      setCustomerForm({ first_name: '', last_name: '', email: '', phone: '', marketing_consent: false });
      fetchCustomers();
      fetchLoyaltyStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create customer');
    } finally {
      setSubmitting(false);
    }
  };

  // Adjust points
  const handleAdjustPoints = async () => {
    if (!pointsAdjustment.points || !pointsAdjustment.reason) {
      toast.error('Please fill all fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/api/customers/${selectedCustomer.id}/points/adjust`, {
        points: parseInt(pointsAdjustment.points),
        reason: pointsAdjustment.reason,
      });
      toast.success('Points adjusted successfully');
      setPointsDialogOpen(false);
      setPointsAdjustment({ points: '', reason: '' });
      fetchCustomers();
      fetchLoyaltyStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to adjust points');
    } finally {
      setSubmitting(false);
    }
  };

  const getTierColor = (tier) => {
    const tierInfo = LOYALTY_TIERS.find(t => t.id === tier);
    return tierInfo?.color || 'bg-amber-700';
  };

  const getTierIcon = (tier) => {
    const tierInfo = LOYALTY_TIERS.find(t => t.id === tier);
    const Icon = tierInfo?.icon || Heart;
    return <Icon className="w-4 h-4" />;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nb-NO', { 
      style: 'currency', 
      currency: 'NOK',
      maximumFractionDigits: 0 
    }).format(amount || 0);
  };

  // Stats from API or calculate from customers
  const stats = loyaltyStats || {
    total_customers: customers.length,
    total_points_outstanding: customers.reduce((sum, c) => sum + (c.loyalty_points || 0), 0),
    total_customer_spend: customers.reduce((sum, c) => sum + (c.total_spend || 0), 0),
    tier_distribution: {
      bronze: customers.filter(c => c.loyalty_tier === 'bronze').length,
      silver: customers.filter(c => c.loyalty_tier === 'silver').length,
      gold: customers.filter(c => c.loyalty_tier === 'gold').length,
      platinum: customers.filter(c => c.loyalty_tier === 'platinum').length,
    },
  };

  return (
    <div data-testid="customers-page" className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl md:text-3xl text-brand-charcoal">Customers & Loyalty</h1>
          <p className="text-gray-500 text-xs sm:text-sm mt-1">
            {currentOutlet?.name || 'All Outlets'} - Customer profiles and loyalty program
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchCustomers(); fetchLoyaltyStats(); }}>
            <RefreshCcw className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button 
            className="bg-brand-orange hover:bg-brand-orange/90" 
            size="sm"
            onClick={() => setCreateDialogOpen(true)}
            data-testid="add-customer-btn"
          >
            <UserPlus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Add Customer</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_customers}</p>
                <p className="text-sm text-gray-500">Total Customers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Star className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_points_outstanding?.toLocaleString()}</p>
                <p className="text-sm text-gray-500">Points Outstanding</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(stats.total_customer_spend)}</p>
                <p className="text-sm text-gray-500">Total Spend</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Gift className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {(stats.tier_distribution?.gold || 0) + (stats.tier_distribution?.platinum || 0)}
                </p>
                <p className="text-sm text-gray-500">VIP Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="customers" data-testid="tab-customers">All Customers</TabsTrigger>
          <TabsTrigger value="loyalty" data-testid="tab-loyalty">Loyalty Program</TabsTrigger>
        </TabsList>

        {/* Customers Tab */}
        <TabsContent value="customers" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by name, email, phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="customer-search"
                  />
                </div>
                <div className="flex gap-2">
                  {['all', ...LOYALTY_TIERS.map(t => t.id)].map(tier => (
                    <Button
                      key={tier}
                      variant={tierFilter === tier ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setTierFilter(tier)}
                      className={tierFilter === tier ? 'bg-brand-orange hover:bg-brand-orange/90' : ''}
                    >
                      {tier === 'all' ? 'All' : tier.charAt(0).toUpperCase() + tier.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : customers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No customers found</p>
                  <Button 
                    variant="link" 
                    className="mt-2"
                    onClick={() => setCreateDialogOpen(true)}
                  >
                    Add your first customer
                  </Button>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <div className="space-y-2">
                    {customers.map((customer) => (
                      <div
                        key={customer.id}
                        className="flex items-center gap-4 p-4 rounded-lg border hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => {
                          setSelectedCustomer(customer);
                          setViewDialogOpen(true);
                        }}
                        data-testid={`customer-${customer.id}`}
                      >
                        {/* Avatar */}
                        <div className={cn(
                          "w-12 h-12 rounded-full flex items-center justify-center text-white font-bold",
                          getTierColor(customer.loyalty_tier)
                        )}>
                          {(customer.first_name?.[0] || '?').toUpperCase()}
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{customer.full_name || `${customer.first_name} ${customer.last_name || ''}`}</p>
                            <Badge className={cn(getTierColor(customer.loyalty_tier), 'text-white text-xs')}>
                              {getTierIcon(customer.loyalty_tier)}
                              <span className="ml-1">{customer.loyalty_tier}</span>
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                            {customer.email && (
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {customer.email}
                              </span>
                            )}
                            {customer.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {customer.phone}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Stats */}
                        <div className="text-right">
                          <p className="font-bold text-purple-600">{customer.loyalty_points?.toLocaleString()} pts</p>
                          <p className="text-sm text-gray-500">{customer.visit_count || 0} visits</p>
                        </div>
                        
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Loyalty Tab */}
        <TabsContent value="loyalty" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Tier Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Tier Distribution</CardTitle>
                <CardDescription>Members by loyalty tier</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {LOYALTY_TIERS.map(tier => {
                    const count = stats.tier_distribution?.[tier.id] || 0;
                    const percentage = stats.total_customers > 0 
                      ? (count / stats.total_customers * 100) 
                      : 0;
                    
                    return (
                      <div key={tier.id}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className={cn("w-8 h-8 rounded-full flex items-center justify-center text-white", tier.color)}>
                              <tier.icon className="w-4 h-4" />
                            </div>
                            <span className="font-medium">{tier.name}</span>
                          </div>
                          <span className="text-sm text-gray-500">{count} members</span>
                        </div>
                        <Progress value={percentage} className="h-2" />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Tier Benefits */}
            <Card>
              <CardHeader>
                <CardTitle>Tier Benefits</CardTitle>
                <CardDescription>Rewards by loyalty level</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Heart className="w-4 h-4 text-amber-700" />
                      <span className="font-medium">Bronze</span>
                      <Badge variant="outline">0+ points</Badge>
                    </div>
                    <p className="text-sm text-gray-600">Welcome bonus, birthday reward</p>
                  </div>
                  <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Award className="w-4 h-4 text-gray-500" />
                      <span className="font-medium">Silver</span>
                      <Badge variant="outline">1,000+ points</Badge>
                    </div>
                    <p className="text-sm text-gray-600">5% off, priority seating</p>
                  </div>
                  <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="w-4 h-4 text-yellow-600" />
                      <span className="font-medium">Gold</span>
                      <Badge variant="outline">5,000+ points</Badge>
                    </div>
                    <p className="text-sm text-gray-600">10% off, free dessert, exclusive events</p>
                  </div>
                  <div className="p-3 rounded-lg bg-purple-50 border border-purple-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Crown className="w-4 h-4 text-purple-600" />
                      <span className="font-medium">Platinum</span>
                      <Badge variant="outline">15,000+ points</Badge>
                    </div>
                    <p className="text-sm text-gray-600">15% off, VIP lounge, personal concierge</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Customer Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-brand-orange" />
              Add New Customer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input
                  placeholder="First name"
                  value={customerForm.first_name}
                  onChange={(e) => setCustomerForm({ ...customerForm, first_name: e.target.value })}
                  data-testid="customer-first-name"
                />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input
                  placeholder="Last name"
                  value={customerForm.last_name}
                  onChange={(e) => setCustomerForm({ ...customerForm, last_name: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="email@example.com"
                value={customerForm.email}
                onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                placeholder="+47 XXX XX XXX"
                value={customerForm.phone}
                onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="marketing"
                checked={customerForm.marketing_consent}
                onChange={(e) => setCustomerForm({ ...customerForm, marketing_consent: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="marketing" className="text-sm">Consent to marketing communications</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateCustomer} 
              disabled={submitting}
              className="bg-brand-orange hover:bg-brand-orange/90"
              data-testid="create-customer-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Customer Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Customer Details</DialogTitle>
          </DialogHeader>
          {selectedCustomer && (
            <div className="space-y-4 py-4">
              {/* Profile Header */}
              <div className="flex items-center gap-4">
                <div className={cn(
                  "w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold",
                  getTierColor(selectedCustomer.loyalty_tier)
                )}>
                  {(selectedCustomer.first_name?.[0] || '?').toUpperCase()}
                </div>
                <div>
                  <h3 className="text-xl font-bold">{selectedCustomer.full_name}</h3>
                  <Badge className={cn(getTierColor(selectedCustomer.loyalty_tier), 'text-white')}>
                    {getTierIcon(selectedCustomer.loyalty_tier)}
                    <span className="ml-1 capitalize">{selectedCustomer.loyalty_tier}</span>
                  </Badge>
                </div>
              </div>
              
              {/* Points */}
              <div className="p-4 bg-purple-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Loyalty Points</p>
                    <p className="text-3xl font-bold text-purple-600">{selectedCustomer.loyalty_points?.toLocaleString()}</p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setPointsDialogOpen(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Adjust
                  </Button>
                </div>
              </div>
              
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Email</p>
                  <p className="font-medium">{selectedCustomer.email || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Phone</p>
                  <p className="font-medium">{selectedCustomer.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Total Spend</p>
                  <p className="font-medium">{formatCurrency(selectedCustomer.total_spend)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Visit Count</p>
                  <p className="font-medium">{selectedCustomer.visit_count || 0} visits</p>
                </div>
                <div>
                  <p className="text-gray-500">First Visit</p>
                  <p className="font-medium">
                    {selectedCustomer.first_visit_date 
                      ? new Date(selectedCustomer.first_visit_date).toLocaleDateString()
                      : '-'
                    }
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Last Visit</p>
                  <p className="font-medium">
                    {selectedCustomer.last_visit_date 
                      ? new Date(selectedCustomer.last_visit_date).toLocaleDateString()
                      : '-'
                    }
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Adjust Points Dialog */}
      <Dialog open={pointsDialogOpen} onOpenChange={setPointsDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-purple-600" />
              Adjust Points
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-500">
              Current balance: <strong>{selectedCustomer?.loyalty_points?.toLocaleString()}</strong> points
            </p>
            <div>
              <Label>Points (+ to add, - to deduct)</Label>
              <Input
                type="number"
                placeholder="e.g. 100 or -50"
                value={pointsAdjustment.points}
                onChange={(e) => setPointsAdjustment({ ...pointsAdjustment, points: e.target.value })}
                data-testid="points-amount"
              />
            </div>
            <div>
              <Label>Reason *</Label>
              <Input
                placeholder="e.g. Birthday bonus, Correction"
                value={pointsAdjustment.reason}
                onChange={(e) => setPointsAdjustment({ ...pointsAdjustment, reason: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPointsDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleAdjustPoints} 
              disabled={submitting}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Adjust Points
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CustomersPage;
